import React, { useState, useEffect } from 'react';
import type { PropsWithChildren } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from '@vlabs/icons';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import classNames from 'classnames';

import './FoldableTag.sass';
import { FoldableEntityEmpty } from '../foldable-entity';

export interface FoldableTagProps extends WithClassNameProp, WithDataTestid, PropsWithChildren {
  heading: React.ReactNode;
  actions?: React.ReactNode;
  isOpen?: boolean;
}

const displayName = 'FoldableTag';

function FoldableTag({
  heading,
  actions = undefined,
  className = undefined,
  'data-testid': dataTestId = undefined,
  children = undefined,
  isOpen: open = true,
}: FoldableTagProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(open);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const hasChildren = React.Children.toArray(children).length > 0;
  let content = children;

  if (!hasChildren) {
    content = <FoldableEntityEmpty />;
  }

  return (
    <div
      className={classNames('FoldableTag', className)}
      data-testid={dataTestId}
    >
      <div
        className="FoldableTag__Heading"
        data-testid={`${dataTestId}-Heading`}
      >
        <button
          className="FoldableTag__Toggle"
          onClick={() => setIsOpen((prev) => !prev)}
          type="button"
        >
          {heading}
          {isOpen ? <ChevronUpIcon className="FoldableTag__Icon" /> : <ChevronDownIcon className="FoldableTag__Icon" />}
        </button>
        {actions}
      </div>
      <div
        className={classNames('FoldableTag__Data', {
          FoldableTag__Data_open: isOpen,
        })}
        data-testid={`${dataTestId}-Data`}
      >
        <div className="FoldableTag__DataInner">
          {content}
        </div>
      </div>
    </div>
  );
}

FoldableTag.displayName = displayName;

export { FoldableTag };
